import React from "react";
import {
    Box,
    Heading,
    Text,
    chakra,
    AspectRatio,
    Stack,
} from "@chakra-ui/react";
import SectionCard from "../components/SectionCard";
import Highlight from "../components/Highlight";

const TrainingAidsPage = () => {
    return (
        <Box bg="gray.50" py={8}>
            {/* Header Section: Image left, Text right */}
            <SectionCard flexDirection="row" delay={0.1}>
                <Box flex="2" textAlign={{base: "center", md: "left"}}>
                    <chakra.img
                        src="./CAAT_TrainingAids_Logo.png"
                        alt="Training Aids Logo"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Training Aids – Tools That Support the Trainer
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Good training requires timing, clarity, and focus — not just from the animal, but from the
                            trainer as well.
                            That’s why we explore tools that{" "}
                            <Highlight>make training easier, more precise, and more flexible</Highlight>.
                        </Text>
                        <Text fontSize="md">
                            These aids aren’t about collecting data — they’re about helping you stay in the moment,
                            respond at the right time,
                            and focus fully on your dog. Whether working in the field or in a structured session, the
                            right tool can make all the difference.
                        </Text>
                        <Text fontSize="md">
                            Each aid presented here was developed with one goal in mind: to give trainers{" "}
                            <Highlight>more freedom to focus on the dog</Highlight>, while the tech handles the rest.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>

            {/* Clicker Section: Use row-reverse to alternate (Text left, Image right) */}
            <SectionCard flexDirection="row-reverse" delay={0.2}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./3BClicker_2.jpg"
                        alt="Bluetooth Clicker"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        2-Button Bluetooth Clicker – Hands-On Precision, Seamlessly Connected
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Recording training data while working with a dog is challenging — it pulls focus, interrupts
                            flow, and often gets skipped altogether.
                            So why not let the tool that’s already in your hand do the work?
                        </Text>
                        <Text fontSize="md">
                            Our <Highlight>2-button Bluetooth clicker</Highlight> looks and feels like a standard box
                            clicker, but adds powerful functionality beneath the surface.
                            With two distinct buttons, trainers can easily mark both successful trials and resets — all
                            without needing to reach for a notebook or a phone.
                        </Text>
                        <Text fontSize="md">
                            The clicker connects directly to the <Highlight>PlanTrainDoc app</Highlight> via Bluetooth,
                            enabling{" "}
                            <Highlight>automatic, real-time recording</Highlight> of every event. This creates a
                            seamless training experience where timing remains perfect, and the full session history is
                            captured effortlessly.
                        </Text>
                        <Text fontSize="md">
                            By combining the familiar simplicity of a classic clicker with modern connectivity, this
                            tool helps trainers stay focused on the dog — while the data takes care of itself.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>

            {/* Treat Dispenser Section: Use row (Media left, Text right) */}
            <SectionCard flexDirection="row" delay={0.3}>
                <Box flex="2">
                    <AspectRatio ratio={16 / 9}>
                        <iframe
                            title="Treat Dispenser Prototype"
                            src="https://www.youtube.com/embed/ANZhBDHkI1o"
                            allowFullScreen
                        />
                    </AspectRatio>
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Bluetooth Treat Dispenser – Reinforcement at a Distance
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Delivering reinforcement at the right moment is key to effective training — but doing so at
                            a distance has always been a challenge.
                            That’s exactly what our{" "}
                            <Highlight>Bluetooth-controlled treat dispenser</Highlight> is designed to solve.
                        </Text>
                        <Text fontSize="md">
                            Still in its early prototype stage, this device allows trainers to deliver rewards remotely,
                            helping to strengthen behaviors
                            without needing to be physically next to the dog.
                        </Text>
                        <Text fontSize="md">
                            Even better: the dispenser integrates directly into the <Highlight>PlanTrainDoc
                            app</Highlight>, so you can use the same training interface you’re already working with.
                            No need to switch tools, juggle remotes, or interrupt the session flow.
                        </Text>
                        <Text fontSize="md">
                            Whether you're reinforcing a successful send-away or building independent behavior at a
                            distance, this tool adds{" "}
                            <Highlight>timely, consistent reinforcement</Highlight> to your training toolkit — all
                            without stepping in.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>
        </Box>
    );
};

export default TrainingAidsPage;
